import { getMessagesForConversation, putMessages } from '@/utils/db/messages.js'
// messageService import removed from loadInitialMessages as it's not used there
// import messageService from "@/services/message.service"; // Keep for loadOlderMessages

/**
 * Loads the initial batch of messages for a conversation,
 * primarily from IndexedDB, assuming they are sorted oldest first by the DB function.
 * @param {string} conversationId
 * @param {number} limit - Max messages to load
 * @returns {Promise<{messages: Array, hasMore: boolean}>}
 */
export async function loadInitialMessages(conversationId, limit = 200) {
    console.log(`Helper: Loading initial messages for ${conversationId}.`)

    let initialMessages = []
    let finalHasMore = false

    try {
        console.log(
            `Helper: Loading initial messages for ${conversationId} from DB...`
        )
        // 1. Load from DB (which now returns oldest first)
        const dbMessagesResult = await getMessagesForConversation(
            conversationId,
            limit
        )

        // 2. Assign directly - NO NEED TO SORT HERE ANYMORE
        initialMessages = dbMessagesResult.messages

        finalHasMore = dbMessagesResult.hasMoreMessages // DB's estimate

        // --- NEW LOGIC START ---
        // If DB says "no more" but we have some messages, it might just be that we hit the sync limit,
        // but the server has more. We should always verify with the API to allow "Load More" to appear.
        if (!finalHasMore && initialMessages.length > 0) {
            const oldestMessage = initialMessages[0]
            if (oldestMessage && oldestMessage.createdAt) {
                console.log(
                    `Helper: DB says no more, but verifying with API for messages before ${oldestMessage.createdAt}...`
                )
                try {
                    // Dynamically import to avoid circular deps if any
                    const messageService = (
                        await import('@/services/api/message.service.js')
                    ).default

                    // Ask for just 1 older message to see if it exists
                    const apiCheck =
                        await messageService.getMessagesFromConversation(
                            conversationId,
                            1,
                            oldestMessage.createdAt,
                            false
                        )

                    if (
                        apiCheck &&
                        (apiCheck.hasMoreMessages ||
                            (apiCheck.messages && apiCheck.messages.length > 0))
                    ) {
                        console.log(
                            'Helper: API check confirms there ARE older messages. Overriding hasMore to true.'
                        )
                        finalHasMore = true
                    } else {
                        console.log(
                            'Helper: API check confirms NO older messages.'
                        )
                    }
                } catch (imgCheckErr) {
                    console.warn(
                        'Helper: Failed to key check API for older messages:',
                        imgCheckErr
                    )
                }
            }
        }
        // --- NEW LOGIC END ---

        console.log(
            `Helper: Loaded ${initialMessages.length} messages from DB (already sorted). DB HasMore: ${finalHasMore}`
        )

        return { messages: initialMessages, hasMore: finalHasMore }
    } catch (error) {
        console.error(
            `Helper Error: Failed to load initial messages for ${conversationId}:`,
            error
        )
        // Return empty on error to prevent breaking the store
        return { messages: [], hasMore: false }
    }
}

/**
 * Loads an older batch of messages for a conversation,
 * combining data from IndexedDB and the API if necessary.
 * @param {string} conversationId
 * @param {string} beforeTimestamp - Timestamp of the oldest currently loaded message.
 * @param {number} limit - Max messages per source (DB/API)
 * @param {Array} currentMessages - The current messages array (to check for duplicates).
 * @returns {Promise<{newMessages: Array, hasMore: boolean}>}
 */
export async function loadOlderMessages(
    conversationId,
    beforeTimestamp,
    limit = 100,
    currentMessages
) {
    console.log(
        `Helper: Loading older messages for ${conversationId} before ${beforeTimestamp}...`
    )
    let dbOlderMessages = []
    let apiOlderMessages = []
    let finalHasMore = false // Start assuming false unless DB/API indicates otherwise

    try {
        // 1. Load older from DB
        console.log(`Helper: Loading older messages from DB...`)
        const dbMessagesResult = await getMessagesForConversation(
            conversationId,
            limit,
            beforeTimestamp
        )
        // Assign directly - NO NEED TO SORT DB BATCH HERE ANYMORE
        dbOlderMessages = dbMessagesResult.messages
        finalHasMore = dbMessagesResult.hasMoreMessages // Initial 'hasMore' based on DB
        console.log(
            `Helper: Loaded ${dbOlderMessages.length} older messages from DB (already sorted). DB HasMore: ${finalHasMore}`
        )

        // 2. Check API ONLY if DB didn't return a full batch OR DB explicitly says there are more
        //    AND we have a valid beforeTimestamp to query against.
        const shouldCheckApi =
            beforeTimestamp && (dbOlderMessages.length < limit || finalHasMore)

        if (shouldCheckApi) {
            console.log(
                `Helper: Checking API for older messages before ${beforeTimestamp} (DB count: ${dbOlderMessages.length}, DB hasMore: ${finalHasMore})...`
            )
            try {
                // Dynamically import messageService only when needed for API call
                const messageService = (
                    await import('@/services/api/message.service.js')
                ).default
                const apiMessagesResult =
                    await messageService.getMessagesFromConversation(
                        conversationId,
                        limit,
                        beforeTimestamp,
                        false // Don't mark as read when loading older
                    )
                // *** IMPORTANT: Assume API might NOT return sorted data ***
                // Sort the API results ascending (oldest first) here
                apiOlderMessages = (apiMessagesResult.messages || []).sort(
                    (a, b) =>
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime()
                )
                finalHasMore = apiMessagesResult.hasMoreMessages // API is the definitive source for 'hasMore' when checked

                if (apiOlderMessages.length > 0) {
                    console.log(
                        `Helper: Received and sorted ${apiOlderMessages.length} older messages from API. API HasMore: ${finalHasMore}`
                    )
                    // Add newly fetched API messages to DB (async, fire-and-forget)
                    putMessages(apiOlderMessages).catch((err) =>
                        console.error(
                            'Helper DB Error: putting older API messages:',
                            err
                        )
                    )
                } else {
                    console.log(
                        `Helper: API check found no older messages. API HasMore: ${finalHasMore}`
                    )
                }
            } catch (apiError) {
                console.error(
                    `Helper: API call failed during loadOlderMessages for ${conversationId}:`,
                    apiError
                )
            }
        } else {
            console.log(
                `Helper: Skipping API check for older messages. (Timestamp: ${beforeTimestamp}, DB count: ${dbOlderMessages.length}, DB hasMore: ${finalHasMore})`
            )
        }

        // 3. Combine *new* DB and API batches and filter duplicates vs current list
        // DB messages are already sorted oldest-first. API messages were sorted above.
        const combinedNewBatch = [...dbOlderMessages, ...apiOlderMessages]
        const existingIds = new Set(currentMessages.map((m) => m._id))
        const uniqueMessageMap = new Map()

        combinedNewBatch.forEach((m) => {
            if (!existingIds.has(m._id)) {
                uniqueMessageMap.set(m._id, m)
            }
        })

        // Convert map values back to array.
        // Since both dbOlderMessages and apiOlderMessages were sorted oldest-first,
        // and we combined them in that order, the combined list *should* be mostly sorted.
        // However, to be absolutely safe, especially if timestamps could be identical,
        // a final sort is recommended before prepending.
        const uniqueNewOlderMessages = Array.from(
            uniqueMessageMap.values()
        ).sort(
            (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
        )

        console.log(
            `Helper: Combined older messages. Found ${uniqueNewOlderMessages.length} unique new ones. Final HasMore: ${finalHasMore}`
        )

        return { newMessages: uniqueNewOlderMessages, hasMore: finalHasMore }
    } catch (error) {
        console.error(
            `Helper Error: Failed to load older messages for ${conversationId}:`,
            error
        )
        // Return empty on error
        return { newMessages: [], hasMore: false }
    }
}
